/** Generic message shown when a request fails without a backend-provided message. */
const CONNECTION_ERROR_MESSAGE = 'Unable to connect to the server. Please try again later.';

/**
 * Maps profile state to the UserUpdate payload shape for PATCH /users/{userId}.
 * Email is intentionally omitted - it is immutable via this endpoint.
 * @param {Object} profile - The local profile state
 * @param {string} profile.firstName - User's first name
 * @param {string} profile.lastName - User's last name
 * @param {string} profile.phoneNumber - User's phone number
 * @param {Object} profile.address - User's address fields
 * @param {string|null} fireDeptId - The currently selected fire department id, or null if unselected
 * @returns {Object} A UserUpdate-shaped payload without an email field
 */
export function toUserUpdatePayload(profile, fireDeptId) {
  return {
    firstName: profile.firstName,
    lastName: profile.lastName,
    phoneNumber: profile.phoneNumber,
    address: {
      streetAddr: profile.address.streetAddr,
      street2Addr: profile.address.street2Addr,
      cityAddr: profile.address.cityAddr,
      stateProvinceAddr: profile.address.stateProvinceAddr,
      postalCodeAddr: profile.address.postalCodeAddr,
      countryAddr: profile.address.countryAddr,
    },
    fireDeptId: fireDeptId ?? null,
  };
}

/**
 * Maps notification preference rows to the UserNotificationPreferencesPatch payload shape
 * for PATCH /users/notifications/{userId}, stripping UI-only fields.
 * @param {Array<Object>} notificationPreferences - The local notification preference rows
 * @returns {Object} A UserNotificationPreferencesPatch-shaped payload
 */
export function toNotificationPreferencesPatch(notificationPreferences) {
  return {
    preferences: notificationPreferences.map(({ topic, emailEnabled, smsEnabled, pushEnabled }) => ({
      topic,
      emailEnabled,
      smsEnabled,
      pushEnabled,
    })),
  };
}

/** Profile fields read directly off the top-level UserResponse, defaulted to an empty string. */
const PROFILE_TEXT_FIELDS = ['firstName', 'lastName', 'email', 'phoneNumber'];

/** Address fields read off UserResponse.address, defaulted to an empty string. */
const PROFILE_ADDRESS_FIELDS = [
  'streetAddr',
  'street2Addr',
  'cityAddr',
  'stateProvinceAddr',
  'postalCodeAddr',
  'countryAddr',
];

/**
 * Builds an object with the given keys read off source, each defaulted to an empty string.
 * @param {Object} source - The object to read fields from
 * @param {Array<string>} fields - The field names to extract
 * @returns {Object} An object containing the extracted, defaulted fields
 */
function pickWithEmptyDefaults(source, fields) {
  return Object.fromEntries(fields.map((field) => [field, source[field] || '']));
}

/**
 * Maps a UserResponse into the local profile state shape.
 * @param {Object} userResponse - The UserResponse returned by GET /users/{userId}
 * @returns {Object} The profile state shape used by the Profile page, including fireDeptId (nullable)
 */
export function toProfileState(userResponse) {
  const address = userResponse.address || {};
  return {
    ...pickWithEmptyDefaults(userResponse, PROFILE_TEXT_FIELDS),
    address: pickWithEmptyDefaults(address, PROFILE_ADDRESS_FIELDS),
    // Grab saved Fire Department ID, otherwise null if none
    fireDeptId: userResponse.fireDeptId || null,
  };
}

/**
 * Reconciles a user's saved fire department id against the fetched list of fire departments.
 * If the saved id has no matching entry in the list (e.g. a stale or deleted reference),
 * resolves to null rather than selecting a nonexistent department.
 * @param {string|null} fireDeptId - The user's saved fire department id, or null
 * @param {Array<Object>} fireDepartments - The fetched list of FireDepartment objects
 * @returns {string|null} The fireDeptId if it matches a fetched department, otherwise null
 */
export function resolveSelectedFireDeptId(fireDeptId, fireDepartments) {
  if (!fireDeptId) {
    return null;
  }

  const hasMatch = (fireDepartments || []).some((department) => department.id === fireDeptId);
  return hasMatch ? fireDeptId : null;
}

/**
 * Merges notification preferences returned by the API onto a set of default rows,
 * matching by topic. Topics absent from the response keep their default values.
 * @param {Array<Object>} defaults - The default notification preference rows (with label/description)
 * @param {Array<Object>} responsePreferences - The preferences array from UserNotificationPreferences
 * @returns {Array<Object>} A new array of notification preference rows, in defaults' order
 */
export function mergeNotificationPreferences(defaults, responsePreferences) {
  const responseByTopic = new Map((responsePreferences || []).map((pref) => [pref.topic, pref]));

  return defaults.map((defaultPref) => {
    const responsePref = responseByTopic.get(defaultPref.topic);
    if (!responsePref) {
      return defaultPref;
    }

    return {
      ...defaultPref,
      emailEnabled: responsePref.emailEnabled,
      smsEnabled: responsePref.smsEnabled,
      pushEnabled: responsePref.pushEnabled,
    };
  });
}

/**
 * Extracts a human-readable error message from a generated-client error object.
 * FastAPI's default HTTPException handler wraps the BadRequestResponse schema
 * from fireBridge.yaml in a `detail` envelope, so the message is expected at
 * `{ response: { body: { detail: { message } } } }`. The unwrapped
 * `{ response: { body: { message } } }` shape is checked as a fallback in case
 * an endpoint ever returns it directly. Network and other unexpected errors
 * (which carry technical, non-user-friendly text in `error.message`) are
 * replaced with a generic connection-failure message.
 * @param {Object} error - The error object rejected by a generated-client API call
 * @param {string} fallback - The message to use if no other message is available
 * @returns {string} A human-readable error message
 */
export function extractErrorMessage(error, fallback) {
  return (
    error?.response?.body?.detail?.message ||
    error?.response?.body?.message ||
    fallback ||
    CONNECTION_ERROR_MESSAGE
  );
}
